import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFacebook, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';

import type {
  AchievementFilter,
  AchievementFilterOption,
} from '@/modules/players/v3/types/achievements';

export type AchievementSharePlatform = {
  name: string;
  icon: IconDefinition;
  getUrl: (url: string, text: string) => string;
};

export const ACHIEVEMENT_SHARE_LABEL = formatText({ text: 'Share', role: TextRole.ButtonLabel });

export const UNCELEBRATED_ACHIEVEMENTS_QUERY_KEY = 'uncelebrated-achievements';

export const CELEBRATION_DELAY_MS = 2000;
export const CELEBRATION_MAX_LIST_ITEMS = 2;

export const ACHIEVEMENTS_STALE_TIME_MS = 5 * 60 * 1000;
export const ACHIEVEMENTS_CACHE_TIME_MS = 10 * 60 * 1000;

export const ACHIEVEMENT_CATEGORY_PAGE_SIZE = 16;

export const LATEST_ACHIEVEMENTS_MOBILE_VISIBLE = 5;

export const LATEST_ACHIEVEMENTS_DESKTOP_VISIBLE = 8;

export const LATEST_ACHIEVEMENTS_INLINE_VISIBLE_TABLET = 2;

export const LATEST_ACHIEVEMENTS_INLINE_VISIBLE_LG = 3;

export const LATEST_ACHIEVEMENTS_INLINE_VISIBLE_XL = 4;

export const DASHBOARD_WIDGET_STACK_OVERLAP_PX = 108;

export const ACHIEVEMENT_FILTER_OPTIONS: AchievementFilterOption[] = [
  { label: 'Latest', value: 'latest' },
  { label: 'Almost there', value: 'almost_there' },
  { label: 'Not started', value: 'not_started' },
  { label: 'All', value: 'all' },
];

export const DEFAULT_ACHIEVEMENT_FILTER: AchievementFilter = 'latest';

/** Persists the selected achievement filter so it's restored when the user returns to the page. */
export const ACHIEVEMENTS_FILTER_COOKIE = 'pb_achievements_filter';

export const PLAYER_ACHIEVEMENTS_QUERY_KEY = 'player-achievements';

export const ACHIEVEMENT_COUNT_BADGE_CLASS_NAME =
  'bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-full px-2.5 py-0.5 whitespace-nowrap';

export const ACHIEVEMENT_DETAIL_MODAL_CONTENT_CLASSNAME =
  'md:!max-w-[380px] !p-0 !gap-0 !border-0 overflow-hidden bg-white';

export const ACHIEVEMENT_DETAIL_MODAL_HEADER_GRADIENT_NOT_STARTED =
  'linear-gradient(180deg, #667085 0%, #F2F4F7 100%)';

export const ACHIEVEMENT_DETAIL_MODAL_HEADER_GRADIENT_ACTIVE =
  'linear-gradient(180deg, #5925DC 7.32%, #230E58 113.53%)';

export const VERTICAL_MILESTONE_BAR_ROW_HEIGHT_PX = 52;

export const VERTICAL_MILESTONE_BAR_TRACK_WIDTH_PX = 20;

export const ACHIEVEMENT_SHARE_PLATFORMS: AchievementSharePlatform[] = [
  {
    name: 'Facebook',
    icon: faFacebook,
    getUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    name: 'X',
    icon: faXTwitter,
    getUrl: (url, text) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    name: 'LinkedIn',
    icon: faLinkedin,
    getUrl: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

export function buildAchievementShareText(achievementName: string): string {
  return `I just earned the "${achievementName}" achievement on Pickleball.com! 🏓 Check out my profile and achievements.`;
}
